import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus } from 'lucide-react'
import Modal from '@/components/Modal'
import QueryStateGate from '@/components/QueryStateGate'
import SubmitOverlay from '@/components/SubmitOverlay'
import { alertDialog } from '@/components/Dialogs'
import { friendlyError } from '@/lib/friendlyError'
import { supabase } from '@/lib/supabase'
import { linkDeliveryToRun, invalidateAfterLink } from '@/lib/linkDeliveryToRun'
import { useCanOpenModule } from '@/hooks/useCanOpenModule'
import { useActiveTenantId } from '@/hooks/useActiveTenantId'
import { useProductionFlow } from '@/lib/useProductionFlow'
import { useFlowManifest } from '@/hooks/useCommodityTemplates'
import type { Run, Delivery } from '@/types/database'

type Candidate = Delivery & { client_ref_no?: string | null; delivery_note_number?: string | null }

function CandidateRow({ d, disabled, onPick }: { d: Candidate; disabled: boolean; onPick: () => void }) {
  return (
  <button type="button"
    disabled={disabled}
    onClick={onPick}
    className="w-full min-h-[44px] text-left bg-surface hover:bg-raised rounded-lg p-3 flex items-center gap-3 disabled:opacity-50">
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        {d.delivery_note_number && (
          <span className="text-caption font-mono bg-raised/60 text-fg rounded-lg px-1.5 py-0.5">DN {d.delivery_note_number}</span>
        )}
        <p className="text-sm font-semibold text-fg">
          {d.bins_remaining} <span className="text-fg-secondary font-normal">of {d.bins_received} bins</span>
        </p>
        {d.client_ref_no && (
          <span className="text-caption font-mono bg-blue-900/40 text-blue-300 rounded-lg px-1.5 py-0.5">{d.client_ref_no}</span>
        )}
      </div>
      <p className="text-caption text-fg-secondary">
        {d.puc ? `PUC ${d.puc}` : '—'}
        {d.block && <span className="ml-1.5">· {d.block}</span>}
        <span className="ml-1.5">· received {new Date(d.received_at).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' })}</span>
      </p>
    </div>
    <Plus size={14} className="text-emerald-400" />
                  </button>
  )
}

type DrenchedLoad = { delivery_id?: string; delivery_note_number?: string | null }

function DrenchedLink({ drenched }: { drenched: DrenchedLoad[] }) {
  const canOpen = useCanOpenModule('deliveries')
  if (!canOpen) return null
  const one = drenched.length === 1 ? drenched[0] : null
  return <Link to={one?.delivery_id ? `/deliveries/${one.delivery_id}` : '/deliveries'} className="underline text-fg">{one ? 'Open the load' : 'Open deliveries'}</Link>
}

function DrenchedNote({ drenched }: { drenched: DrenchedLoad[] }) {
  if (drenched.length === 0) return null
  const one = drenched.length === 1
  return (
    <p className="mt-3 text-caption text-fg-secondary">
      {one ? '1 drenched load' : `${drenched.length} drenched loads`}
      {one && drenched[0].delivery_note_number ? ` (DN ${drenched[0].delivery_note_number})` : ''}
      {' '}of this fruit {one ? 'waits' : 'wait'} for Mark In Stock.{' '}
      <DrenchedLink drenched={drenched} />
    </p>
  )
}

/** The three reads behind the picker: linked ids, candidates, drenched loads. */
function useAddDeliveryReads(run: Run, open: boolean) {
  const tenantId = useActiveTenantId()
  const linkedQ = useQuery({
    queryKey: ['run-linked-deliveries', run.run_id],
    enabled: open,
    queryFn: async () => {
      const { data, error } = await supabase.from('run_deliveries')
        .select('delivery_id').eq('run_id', run.run_id)
      if (error) throw error
      return (data ?? []).map(r => r.delivery_id as string)
    },
  })

  const linkedIds = linkedQ.data ?? []

  const candidatesQ = useQuery({
    queryKey: ['add-delivery-candidates', tenantId, run.run_id, run.grower_id ?? run.grower, run.variety, linkedIds.join(',')],
    enabled: open && linkedQ.isSuccess && !!tenantId,
    queryFn: async () => {
      // Match on grower_id when both sides have one (robust against cached-text
      // drift); fall back to text equality for old rows without one.
      let q = supabase.from('deliveries')
        .select('delivery_id, grower, grower_id, variety, puc, block, bins_received, bins_remaining, status, received_at, intake_path, client_ref_no, delivery_note_number')
        .eq('tenant_id', tenantId!)
        .in('status', ['IN_STOCK', 'RECEIVED'])
        .gt('bins_remaining', 0)
        .is('deleted_at', null)
        .eq('variety', run.variety)
        .order('received_at', { ascending: true })
      q = run.grower_id ? q.eq('grower_id', run.grower_id) : q.eq('grower', run.grower)
      const { data, error } = await q
      if (error) throw error
      return ((data ?? []) as Candidate[]).filter(d => !linkedIds.includes(d.delivery_id))
    },
  })
  // Drenched loads of this fruit are not offered (they are not In Stock yet):
  // say so when one waits. A failed read leaves the line out, never claims none.
  const drenchedQ = useQuery({
    queryKey: ['add-delivery-drenched', tenantId, run.run_id, run.grower_id ?? run.grower, run.variety],
    enabled: open && !!tenantId,
    queryFn: async () => {
      let q = supabase.from('deliveries')
        .select('delivery_id, delivery_note_number')
        .eq('tenant_id', tenantId!)
        .eq('status', 'DRENCHED')
        .gt('bins_remaining', 0)
        .is('deleted_at', null)
        .eq('variety', run.variety)
      q = run.grower_id ? q.eq('grower_id', run.grower_id) : q.eq('grower', run.grower)
      const { data, error } = await q
      if (error) throw error
      return (data ?? []) as { delivery_id: string; delivery_note_number?: string | null }[]
    },
  })
  const drenched = drenchedQ.isSuccess ? drenchedQ.data : []
  return { linkedQ, candidatesQ, drenched }
}

/**
 * F02 UF-04 — add a delivery to a live run. Mounted on the run page's Tipping
 * tab and on the Tipping board (a second mount, so it lives in its own file).
 * The match rule is unchanged: this run's grower + variety, In Stock or
 * Received, bins left. `onAdded` gets the delivery that was just linked (the
 * board opens its tip form).
 */
export function AddDeliveryToRunButton({ run, label = 'Add another delivery to this run', onAdded }: {
  run: Run
  label?: string
  onAdded?: (delivery: Delivery) => void
}) {
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  // Berry runs are fed by RELEASED cooling batches at tip time, never by
  // linking deliveries (mig 822 rejects delivery-direct tips anyway).
  const flow = useProductionFlow()
  const manifest = useFlowManifest(flow)

  const { linkedQ, candidatesQ, drenched } = useAddDeliveryReads(run, open)
  const { data: candidates = [], isLoading } = candidatesQ

  const link = useMutation({
    mutationFn: async (d: Candidate) => {
      await linkDeliveryToRun(supabase, run.run_id, d.delivery_id)
      return d
    },
    onSuccess: (d) => {
      invalidateAfterLink(qc, run.run_id, d.delivery_id)
    },
    onError: (e: Error) => { void alertDialog({ title: "Couldn't add", message: friendlyError(e).message, tone: 'danger' }) },
  })

  // After all hooks (rules-of-hooks): berry runs never link deliveries.
  if (manifest.usesForceCooling) return null

  return (
    <>
      <SubmitOverlay
        isPending={link.isPending}
        isSuccess={link.isSuccess}
        isError={link.isError}
        error={link.error}
        successLabel="Delivery added to run"
        onDismiss={() => link.reset()}
      />
      <button type="button" onClick={() => setOpen(true)}
        className="w-full min-h-[44px] text-sm py-2 rounded-lg border border-dashed border-emerald-700 text-emerald-300 hover:bg-emerald-900/20 font-semibold flex items-center justify-center gap-1.5">
        <Plus size={14} /> {label}
      </button>

      {open && (
        <Modal title={`Add a delivery to ${run.run_number}`}
          subtitle={`${run.grower} · ${run.variety} · In stock or Received, bins left`}
          onClose={() => setOpen(false)} noFooter>
          {linkedQ.isError ? (
            <QueryStateGate query={linkedQ} noun="the deliveries already on this run" empty={null} />
          ) : linkedQ.isPending || isLoading ? (
            <div className="flex justify-center py-12">
              <div className="w-5 h-5 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : candidates.length === 0 ? (
            <QueryStateGate query={candidatesQ} noun="in-stock deliveries" empty={
              <p className="text-sm text-fg-muted italic text-center py-8">
                No in-stock deliveries from {run.grower} matching {run.variety}. They must be RECEIVED or IN_STOCK with bins remaining.
              </p>
            } />
          ) : (
            <div className="space-y-2">
              {candidates.map(d => (
                <CandidateRow key={d.delivery_id} d={d} disabled={link.isPending}
                  onPick={() => link.mutate(d, { onSuccess: (row) => { setOpen(false); onAdded?.(row) } })} />
              ))}
            </div>
          )}
          <DrenchedNote drenched={drenched} />
        </Modal>
      )}
    </>
  )
}
